import React, { memo } from "react";
import Values from "../components/Values";
import TeamImg from "../assets/images/gallery/ecl-hotel.webp";
import eclWay from "../assets/images/NSS/New folder/ecl-way.webp"
import CompanyGallery from "../components/CompanyGallery";
import Awards from "../components/Awards";
import SocialFeed from "../components/SocialFeed";

// Defers setting backgroundImage until the section scrolls into view.
// Prevents offscreen hero images from blocking page load.
function useLazyBackground(imageUrl) {
  const ref = React.useRef(null);
  const [bgUrl, setBgUrl] = React.useState(null);

  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBgUrl(imageUrl);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [imageUrl]);

  return { ref, bgUrl };
}


const CERTIFICATIONS = [
  {
    src: "/ISO9001-2015.png",
    alt: "ISO 9001:2015 Certificate",
    label: "ISO 9001:2015",
  },
  {
    src: "/ISO27001-2022.png",
    alt: "ISO 27001:2022 Certificate",
    label: "ISO 27001:2022",
  },
  {
    src: "/csa.png",
    alt: "CSA Certificate",
    label: "CSA Tier 1 MSP",
  },
  {
    src: "/nita.png",
    alt: "NITA Certificate",
    label: "NITA Certified",
  },
  {
    src: "/DATA PROTECTION11.jpg",
    alt: "Data Protection Certificate",
    label: "Data Protection",
  },
];

const CertificationBadge = memo(({ src, alt, label }) => (
  <div className="text-center">
    <img
      src={src}
      alt={alt}
      className="img-fluid"
      style={{ height: "52px", width: "auto" }}
      loading="lazy"
    />
    <div
      style={{
        fontSize: "0.7rem",
        color: "#4B5563",
        marginTop: "6px",
        lineHeight: 1.3,
        fontWeight: 500,
      }}
    >
      {label}
    </div>
  </div>
));
CertificationBadge.displayName = "CertificationBadge";


/* ─── WHO WE ARE ─── */
function WhoWeAreSection() {
  const { ref: whoRef, bgUrl: whoBg } = useLazyBackground(TeamImg);
  return (
  <section
    ref={whoRef}
    style={{
      backgroundImage: whoBg ? `url(${whoBg})` : undefined,
      backgroundColor: "#0a1a3a",
      backgroundSize: "cover",
      backgroundPosition: "center",
      position: "relative",
    }}
  >
    {/* Dark overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(38, 38, 38, 0.55)",
      }}
    />

    <div className="container py-5" style={{ position: "relative", zIndex: 1 }}>
      <div className="row align-items-center" style={{ minHeight: 520 }}>
        {/* Spacer — left half shows the image */}
        <div className="col-md-5 d-none d-md-block" />

        <div className="col-md-7">
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.97)",
              padding: "40px 44px",
              borderRadius: "12px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              margin: "40px 0",
            }}
          >
            <p
              className="text-uppercase fw-semibold mb-2"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
            >
              About us
            </p>
            <h2
              className="fw-bold mb-3"
              style={{ fontSize: "clamp(2.1rem, 4vw, 3.2rem)", lineHeight: 1.15, color: "#0a1a3a" }}
            >
              ECL is an ISO certified Professional Services technology company.
            </h2>

            <p style={{ color: "#4B5563", lineHeight: 1.75, marginBottom: "0.75rem" }}>
              We deliver IT services such as Hybrid Cloud Infrastructure, Business
              Process Automation, Cyber Resilience and Cybersecurity solutions.
            </p>

            <p style={{ color: "#4B5563", lineHeight: 1.75, marginBottom: "1.5rem" }}>
              In a fast-changing world where technology has disrupted several
              business models, we help businesses stay relevant to their customers
              by deploying innovative technology solutions.
            </p>

            <p
              className="fw-semibold mb-3"
              style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "#6B7280" }}
            >
              We are Certified
            </p>

            <div className="row g-3">
              {CERTIFICATIONS.map((cert) => (
                <div key={cert.alt} className="col">
                  <CertificationBadge {...cert} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
}


/* ─── IMS POLICY ─── */
const IMSPolicySection = () => (
  <section className="py-5" style={{ background: "#f8fafc" }}>
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-lg-8 text-center">
          <p
            className="text-uppercase fw-semibold mb-2"
            style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
          >
            Our commitment
          </p>
          <h2 className="fw-bold mb-4" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}>
            IMS Policy Statement
          </h2>
          <div
            style={{
              width: 48,
              height: 3,
              background: "#009EDC",
              margin: "0 auto 2rem",
              borderRadius: 2,
            }}
          />

          <p style={{ color: "#4B5563", lineHeight: 1.8, marginBottom: "1.25rem" }}>
            Enterprise Computing Limited is committed to providing simplified and
            reliable IT solutions with financial support globally and to being the
            preferred brand in delivering world-class IT solutions. ECL is
            complying with the requirements of ISO 9001:2015 and ISO 27001:2022.
          </p>

          <p style={{ color: "#4B5563", lineHeight: 1.8, marginBottom: "1.25rem" }}>
            Management is committed to continually improving the effectiveness of
            the quality management system and information security management
            system while complying with all applicable statutory and regulatory
            requirements.
          </p>

          <p style={{ color: "#4B5563", lineHeight: 1.8 }}>
            All employees and non-employees of Enterprise Computing Limited are
            committed to protecting information security assets. All personnel must
            ensure due care is exercised in the protection of our knowledge base
            through consistent coordination of security efforts across ECL.
          </p>
        </div>
      </div>
    </div>
  </section>
);


/* ─── CORE VALUES ─── */
const CoreValuesSection = () => (
  <section className="py-5">
    <div className="container text-center">
      <p
        className="text-uppercase fw-semibold mb-2"
        style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
      >
        What drives us
      </p>
      <h2 className="fw-bold mb-2" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}>
        Our guiding principles
      </h2>
      <p style={{ color: "#6B7280", marginBottom: "2.5rem" }}>
        Our core values encompass everything we stand for.
      </p>
      <Values />
    </div>
  </section>
);


/* ─── THE ECL WAY ─── */
function ECLWaySection() {
  const { ref: eclRef, bgUrl: eclBg } = useLazyBackground(eclWay);
  return (
  <section
    ref={eclRef}
    style={{
      backgroundImage: eclBg ? `url(${eclBg})` : undefined,
      backgroundColor: "#0a1a3a",
      backgroundSize: "cover",
      backgroundPosition: "center",
      position: "relative",
    }}
  >
    {/* Overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.6)",
      }}
    />

    <div className="container py-5" style={{ position: "relative", zIndex: 1 }}>
      <div className="text-center mb-5">
        <p
          className="text-uppercase fw-semibold mb-2"
          style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#6dcaf9" }}
        >
          Culture &amp; people
        </p>
        <h2
          className="fw-bold"
          style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", color: "#fff" }}
        >
          The ECL Way
        </h2>
        <p style={{ color: "rgba(255,255,255,0.75)", marginTop: "0.75rem" }}>
          A glimpse into our people, our culture, and the moments that define us.
        </p>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-7">
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.96)",
              padding: "40px 44px",
              borderRadius: "12px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <p style={{ fontSize: "1.1rem", lineHeight: 1.8, color: "#374151", margin: 0 }}>
              <span className="fw-bold" style={{ color: "#0a1a3a" }}>
                ECL is a vibrant mix of young professionals
              </span>{" "}
              where diversity sparks creativity and learning fuels everything we
              do. We blend curiosity, play, and innovation to make tech cool,
              fresh, and unapologetically human. We're building, exploring, and
              redefining what's possible, one brilliant idea at a time.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
}


/* ─── MAIN PAGE ─── */
export default function AboutUsPage() {
  return (
    <>
      <WhoWeAreSection />
      <IMSPolicySection />
      <CoreValuesSection />
      <Awards />
      <ECLWaySection />
      <CompanyGallery />
      <SocialFeed />
    </>
  );
}
